import { STORAGE_API, STORAGE_API_KEY } from './config';
import type { UploadResult } from '@/models/auth';

// Storage-API uses X-API-KEY auth (not Bearer JWT) — the Bearer flow goes
// through issue-api / auth-api, but storage has its own static key. We embed
// it in the bundle deliberately: this app is gated by ALLOWED_EMAILS in
// useAuth, and the key only grants upload (reads are by public storage_id).
async function uploadMultipart<T>(file: File, context: string): Promise<T> {
  const fd = new FormData();
  fd.append('file', file, file.name);
  fd.append('context', context);
  fd.append('is_public', 'true');
  fd.append('analyze', 'false');
  const res = await fetch(`${STORAGE_API}/storage/upload`, {
    method: 'POST',
    headers: { 'X-API-KEY': STORAGE_API_KEY },
    body: fd,
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => res.statusText);
    throw new Error(`storage upload ${res.status}: ${detail.slice(0, 200)}`);
  }
  return (await res.json()) as T;
}

// Historical attachments uploaded before 2026-06-09 live on Linode-storage
// (api-storage.arkturian.com, IDs ≥ ~100000). New uploads after that go to
// arkserver-storage (separate ID-space, ≤ ~50000). Route by ID range until
// IssueAttachment records can carry an explicit storage_host column.
const ARKTURIAN_STORAGE_BASE = 'https://api-storage.arkturian.com';
const ARKTURIAN_ID_THRESHOLD = 100_000;

function resolveStorageBase(storage_id: number): string {
  return storage_id >= ARKTURIAN_ID_THRESHOLD ? ARKTURIAN_STORAGE_BASE : STORAGE_API;
}

export const storageApi = {
  upload: (file: File, context = 'issue-intake') =>
    uploadMultipart<UploadResult>(file, context),
  mediaUrl: (storage_id: number): string =>
    `${resolveStorageBase(storage_id)}/storage/media/${storage_id}`,
};
